'use client';

import RefreshIcon from '@mui/icons-material/Refresh';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { Box, Button, Stack, Theme, Typography } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement, ReactNode, useEffect, useRef } from 'react';
import CopyableText from '../CopyableText';
import { Logo } from '../Logo';
import { StatusIconTile } from '../StatusIconTile/StatusIconTile';

export type ErrorBoundaryAction = {
  label: string;
  onClick: () => void;
  variant: 'contained' | 'outlined' | 'text';
  'data-action'?: string;
};

export type ErrorBoundaryScreenProps = {
  /**
   * Which app is rendering the screen. Kept for the caller's semantics (copy,
   * telemetry); it does NOT drive the layout. The screen is responsive: the
   * same treatment adapts between mobile and desktop regardless of product.
   */
  product: 'wallet' | 'management' | 'integrations';
  headline: string;
  body: string;
  securityNote: string;
  primaryAction: ErrorBoundaryAction;
  secondaryAction?: ErrorBoundaryAction;
  /**
   * A captured error reference to quote to support. The runtime boundaries pass
   * one; the static 404/500 pages have no real reference, so they omit it and
   * the reference footer is not rendered.
   */
  referenceCode?: string;
  showNotified?: boolean;
  notifiedText?: string;
  loopHint?: string;
  /**
   * Sanctioned product logo. Each app injects its own brand (e.g. the Wallet
   * ClarityLogo, which auto-adapts to the theme). Defaults to the shared
   * design-system Logo (the sanctioned Management mark) in the screen's colour mode.
   */
  logo?: ReactNode;
  'data-testid'?: string;
};

function ErrorBoundaryScreenComponent({
  headline,
  body,
  securityNote,
  primaryAction,
  secondaryAction,
  referenceCode,
  showNotified = false,
  notifiedText,
  loopHint,
  logo,
  'data-testid': dataTestId,
}: Readonly<ErrorBoundaryScreenProps>): ReactElement {
  const regionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    regionRef.current?.focus();
  }, []);

  const logoNode = logo ?? <Logo colorMode="auto" layout="horizontal" size="medium" />;

  const actionButtonSx = { width: ['100%', 'auto'] } as const;

  const primaryButton = (
    <Button
      variant={primaryAction.variant}
      onClick={primaryAction.onClick}
      data-action={primaryAction['data-action']}
      sx={actionButtonSx}
    >
      {primaryAction.label}
    </Button>
  );

  const secondaryButton = secondaryAction && (
    <Button
      variant={secondaryAction.variant}
      onClick={secondaryAction.onClick}
      data-action={secondaryAction['data-action']}
      sx={actionButtonSx}
    >
      {secondaryAction.label}
    </Button>
  );

  return (
    <Stack
      data-testid={dataTestId}
      sx={(theme: Theme) => ({
        minHeight: '100vh',
        width: '100%',
        backgroundColor: theme.palette.background.default,
        padding: theme.semantic.spacing.lg,
        boxSizing: 'border-box',
      })}
    >
      {/* Logo as page chrome: centred at the top on mobile, top-left on desktop. */}
      <Box sx={{ display: 'flex', width: '100%', justifyContent: ['center', 'flex-start'] }}>
        {logoNode}
      </Box>

      {/* Centre the screen in the space left below the logo. */}
      <Stack sx={{ flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center' }}>
        <Stack
          ref={regionRef}
          role="alert"
          aria-live="assertive"
          tabIndex={-1}
          sx={(theme: Theme) => ({
            width: '100%',
            maxWidth: [theme.semantic.size.xs, theme.semantic.size.sm],
            alignItems: 'center',
            textAlign: 'center',
            gap: theme.semantic.spacing.base,
            outline: 'none',
            boxSizing: 'border-box',
            // Desktop lifts the content into an elevated card; mobile is
            // full-bleed so it reads as a plain page.
            backgroundColor: ['transparent', theme.palette.background.paper],
            boxShadow: ['none', theme.shadows[2]],
            borderRadius: [0, theme.semantic.borderRadius.lg],
            padding: [0, theme.semantic.spacing['3xl']],
            paddingBottom: [0, theme.semantic.spacing['2xl']],
          })}
        >
          <StatusIconTile
            background={(theme) => theme.palette.surface.brandSubtle}
            iconColor={(theme) => theme.palette.primary.main}
            shape="lg"
            icon={<RefreshIcon aria-hidden />}
          />

          <Typography variant="h4" sx={{ fontWeight: (t: Theme) => t.semantic.fontWeight.semibold }}>
            {headline}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {body}
          </Typography>

          <Box
            sx={(theme: Theme) => ({
              display: 'inline-flex',
              alignItems: 'center',
              gap: theme.semantic.spacing.xs,
              padding: theme.semantic.spacing.xs,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: theme.semantic.borderRadius.round,
            })}
          >
            <VerifiedUserOutlinedIcon
              sx={(theme: Theme) => ({ color: 'primary.main', fontSize: theme.semantic.size['8xs'] })}
              aria-hidden
            />
            <Typography variant="caption" color="text.primary">
              {securityNote}
            </Typography>
          </Box>

          {/* The "we've been notified" reassurance is for a first, one-off crash.
              Once it recurs the loop hint takes over (below the actions) and asks
              the user to contact us, so showing both would sandwich the CTA with
              two conflicting team messages. The loop hint wins. */}
          {showNotified && notifiedText && !loopHint && (
            <Typography variant="body2" color="text.secondary">
              {notifiedText}
            </Typography>
          )}

          {/* DOM order is secondary then primary (house convention): on desktop
              this reads left-to-right as secondary, primary; column-reverse keeps
              the primary on top when the buttons stack on mobile. */}
          <Stack
            direction={['column-reverse', 'row']}
            sx={(theme: Theme) => ({
              width: '100%',
              gap: theme.semantic.spacing.sm,
              justifyContent: 'center',
            })}
          >
            {secondaryButton}
            {primaryButton}
          </Stack>

          {loopHint && (
            <Typography variant="caption" color="text.secondary">
              {loopHint}
            </Typography>
          )}

          {/* Reference footer (hairline as in the design). Only rendered when
              there is a real reference to quote: the runtime boundaries pass a
              captured reference; the static 404/500 pages have none. */}
          {referenceCode && (
            <Stack
              direction="row"
              sx={(theme: Theme) => ({
                width: '100%',
                marginTop: theme.semantic.spacing.sm,
                paddingTop: theme.semantic.spacing.base,
                borderTop: `1px solid ${theme.palette.divider}`,
                alignItems: 'center',
                justifyContent: 'center',
                gap: theme.semantic.spacing.xs,
              })}
            >
              <Typography variant="caption" color="text.secondary">
                Reference
              </Typography>
              {/* Reuse the shared copyable component (self-contained copy + tooltip). */}
              <CopyableText text={referenceCode} tooltipPlacement="top" />
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}

export const ErrorBoundaryScreen = withClarityTheme(ErrorBoundaryScreenComponent);
